<template>
  <article class="note-item">
    <component
      :is="comp"
      :note="note"
      :user-id="userId"
      :membership="membership"
      :public-mode="publicMode"
      @toggle-select="onToggleSelect"
      @tag-selected="onSelectTag"
      @favorite="onAction('favorite', $event)"
      @save="onAction('save', $event)"
      @add-to-collection="onAction('add-to-collection', $event)"
      @add-to-collection-from-collection="onAction('add-to-collection-from-collection', $event)"
      @comments="onAction('comments', $event)"
      @like="onAction('like', $event)"
      @likes="onAction('likes', $event)"
      @pin-edit="onAction('pin-edit', $event)"
      @more="onMoreOptions"
    />

    <!-- Note options menu - lazy loaded on first interaction -->
    <NoteOptions
      v-if="dirty && showOptions"
      v-model="optionsOpen"
      :note="note"
      :user-id="userId"
      :membership="membership"
      :public-mode="publicMode"
      :is-grid-layout="isGridLayout"
      :with-secondary-action="false"
      :with-primary-action="false"
      @comments="onAction('comments', $event)"
      @save="onAction('save', $event)"
      @add-to-collection="onAction('add-to-collection', $event)"
      @tags="onAction('tags', $event)"
      @edit="onAction('edit', $event)"
      @delete="onAction('delete', $event)"
      @share="onAction('share', $event)"
      @pin="onAction('pin', $event)"
      @unpin="onAction('unpin', $event)"
    />

    <!-- Tags filter menu - lazy loaded on first interaction -->
    <NoteTagsFilter
      v-if="dirty"
      v-model="tagsFilterOpen"
      :activator="$el"
      :note="note"
      :user-id="userId"
      :membership="membership"
      :public-mode="publicMode"
      @submit="$emit('filter-tags', $event)"
      @edit="onAction('tags', $event)"
    />
  </article>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits, defineAsyncComponent } from 'vue'
import { useDisplay } from 'vuetify'
import { ITEM_LAYOUT_STYLE } from '@/utils/constants'
import NoteCard from './notes/NoteCard.vue'
import NoteListItem from './notes/NoteListItem.vue'
import NoteHeadline from './notes/NoteHeadline.vue'
import NoteTile from './notes/NoteTile.vue'

// Lazy load optional components
const NoteOptions = defineAsyncComponent(() => import('./NoteOptions.vue'))
const NoteTagsFilter = defineAsyncComponent(() => import('./NoteTagsFilter.vue'))

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  listStyle: {
    type: String,
    default: 'card',
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['action', 'filter-tags'])

const STYLES = {
  [ITEM_LAYOUT_STYLE.cards]: NoteCard,
  [ITEM_LAYOUT_STYLE.list]: NoteListItem,
  [ITEM_LAYOUT_STYLE.headlines]: NoteHeadline,
  [ITEM_LAYOUT_STYLE.tiles]: NoteTile,
}

const dirty = ref(false)
const tagsFilterOpen = ref(false)
const optionsOpen = ref(false)
const display = useDisplay()

// Reset menu state when the row is recycled by vue-virtual-scroller —
// without this, stale local state leaks to a different note.
watch(() => props.note?._id, (newId, oldId) => {
  if (newId !== oldId) {
    dirty.value = false
    optionsOpen.value = false
    tagsFilterOpen.value = false
  }
})

const comp = computed(() => {
  return STYLES[props.listStyle]
})

const isGridLayout = computed(() => {
  return [ITEM_LAYOUT_STYLE.cards, ITEM_LAYOUT_STYLE.tiles].includes(props.listStyle) && display.smAndUp.value
})

const showOptions = computed(() => {
  return true
})

function onSelectTag() {
  dirty.value = true
  tagsFilterOpen.value = true
}

function onMoreOptions() {
  dirty.value = true
  optionsOpen.value = true
}

function onAction(action, event) {
  emit('action', { action, event })
}

function onToggleSelect(event) {
  emit('action', { action: 'toggle-select', event })
}
</script>
